import React from 'react';
import styled from 'styled-components';

type TProps = {
    type?: 'bounce' | 'ripple' | 'spin';
    size?: number;
    color?: string | 'colored' | 'white';
    center?: boolean;
};

const Loader = ({ center = true, color = '#fff', size = 6, type = 'bounce' }: TProps): JSX.Element => {
    switch (type) {
        case 'bounce':
            return (
                <BounceWrapper width={size} center={center}>
                    <BounceChild height={size} width={size} color={color} delay="-0.32s" />
                    <BounceChild height={size} width={size} color={color} delay="-0.16s" />
                    <BounceChild height={size} width={size} color={color} />
                </BounceWrapper>
            );

        case 'ripple':
            return (
                <RippleWrapper>
                    <RippleLoader>
                        <Ripple>
                            <RippleChild color={color} />
                            <RippleChild color={color} />
                        </Ripple>
                    </RippleLoader>
                </RippleWrapper>
            );

        case 'spin':
            return (
                <SpinLoaderWrapper height={size} width={size}>
                    <SpinLoader color={color} />
                </SpinLoaderWrapper>
            );
        default:
            return <div />;
    }
};

/* INDEX
1. Bounce Loader Styles
2. Ripple Loader Styles
3. spin Loader styles
*/

/* 1. Bounce Loader Styles */
type TBounceWrapperProps = { width: number; center: boolean };
const BounceWrapper = styled.div<TBounceWrapperProps>`
    margin: 0;
    width: 80px;
    text-align: center;
    ${({ center }): string => `margin: 0 ${center ? 'auto' : ''};`};

    display: flex;
    flex-direction: row;
    align-items: center;
    ${({ width }): string => `width: ${width * 3 + 12}px`};
`;

type TBounceChildProps = { color: string; height: number; width: number; delay?: string };
const BounceChild = styled.div<TBounceChildProps>`
    width: 20px;
    height: 20px;
    background-color: #333;
    border-radius: 100%;
    display: inline-block;
    margin: 0 2px;
    animation-name: bounce;
    animation-duration: 1.4s;
    animation-fill-mode: ease-in-out;
    animation-iteration-count: infinite;
    animation-direction: both;
    ${({ color, theme }): string => {
        let bgColor = color;
        if (color === 'colored') {
            bgColor = theme.colors.brandSecondary;
        }
        if (color === 'light') {
            bgColor = theme.colors.bgPrimary;
        }
        return `background-color: ${bgColor}`;
    }};
    ${({ height }): string => `height: ${height}px`};
    ${({ width }): string => `width: ${width}px`};
    ${({ delay }): string => (delay ? `animation-delay: ${delay}` : '')};
    @keyframes bounce {
        0% {
            -webkit-transform: scale(0);
            transform: scale(0);
        }
        80% {
            -webkit-transform: scale(0);
            transform: scale(0);
        }
        100% {
            -webkit-transform: scale(0);
            transform: scale(0);
        }
        40% {
            -webkit-transform: scale(1);
            transform: scale(1);
        }
    }
`;

/* 2. Ripple Loader Styles */
const RippleWrapper = styled.div`
    min-height: 100%;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const RippleLoader = styled.div`
    margin-left: -75px;
`;

const Ripple = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
    transform: scale(0.4);
`;

const RippleChild = styled.div`
    box-sizing: content-box;
    position: absolute;
    border-width: 5px;
    border-style: solid;
    opacity: 1;
    border-radius: 50%;
    -webkit-animation: loader-ripple 1s cubic-bezier(0, 0.2, 0.8, 1) infinite;
    animation: loader-ripple 1s cubic-bezier(0, 0.2, 0.8, 1) infinite;
    &:nth-child(1) {
        ${({ theme }): string => `border-color: ${theme.colors.bodyLight}`};
    }
    &:nth-child(2) {
        ${({ theme }): string => `border-color: ${theme.colors.bodyLight}`};
        -webkit-animation-delay: -0.5s;
        animation-delay: -0.5s;
    }
    @keyframes loader-ripple {
        0% {
            top: 90px;
            left: 90px;
            width: 0;
            height: 0;
            opacity: 1;
        }
        100% {
            top: 38px;
            left: 38px;
            width: 100px;
            height: 100px;
            opacity: 0;
        }
    }
`;

type TSpinWrapperProps = { color?: string; height: number; width: number };
const SpinLoaderWrapper = styled.div<TSpinWrapperProps>`
    ${({ height }): string => `height: ${height}px`};
    ${({ width }): string => `width: ${width}px`};
    padding: 5px;
`;
type TSpinProps = { color?: string };
const SpinLoader = styled.div<TSpinProps>`
    border: 5px solid #f3f3f3;
    border-radius: 50%;
    border-top: 5px solid;
    width: 100%;
    height: 100%;
    animation: spin 2s linear infinite;
    ${({ color }): string => {
        return `border-top-color: ${color}`;
    }};

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`;

export default Loader;
